// Layer 1 - 1 year - Sensory cause-and-effect games (10 games total)
import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useGame } from "@/context/GameContext";
import { playSfx } from "@/lib/sounds";
import { t } from "@/lib/translations";
import { Lock } from "lucide-react";

// Helper sound trigger
const playSfxHelper = (name, soundOn) => {
  try {
    playSfx(name, soundOn);
  } catch (e) {
    console.warn("Sound play failed", e);
  }
};

// ==========================================
// 1) Peek-a-boo
// ==========================================
function PeekABoo({ layer, onWin }) {
  const { language, soundOn } = useGame();
  const [hidden, setHidden] = useState(true);
  const [count, setCount] = useState(0);

  const toggle = () => {
    playSfxHelper(hidden ? "giggle" : "tap", soundOn);
    setHidden((h) => !h);
    if (hidden) {
      const newCount = count + 1;
      setCount(newCount);
      if (newCount >= 3) {
        setTimeout(() => onWin(), 800);
        setCount(0);
      }
    }
  };

  return (
    <div className="flex flex-col items-center gap-6 mt-6" data-testid="game-peekaboo">
      <div className="font-display text-2xl sm:text-3xl font-bold text-center text-slate-700">
        {hidden ? t("hide", language) + "..." : t("found_me", language) + " 🎉"}
      </div>
      <motion.button
        onClick={toggle}
        whileTap={{ scale: 0.9 }}
        className="relative w-72 h-72 sm:w-96 sm:h-96 rounded-full overflow-hidden border-[8px] border-white shadow-2xl"
        style={{ background: layer.color }}
        data-testid="peekaboo-btn"
      >
        <AnimatePresence>
          {!hidden && (
            <motion.img
              key="baby"
              src={layer.baby}
              alt="baby"
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.5, opacity: 0 }}
              className="absolute inset-0 w-full h-full object-cover"
              draggable={false}
            />
          )}
          {hidden && (
            <motion.div
              key="hands"
              initial={{ y: -300 }}
              animate={{ y: 0 }}
              exit={{ y: -300 }}
              className="absolute inset-0 flex items-center justify-center text-9xl"
            >
              🙈
            </motion.div>
          )}
        </AnimatePresence>
      </motion.button>
      <div className="font-bold text-slate-500">
        {language === "id" ? "Ketuk untuk Cilukba!" : "Tap for Peek-a-boo!"} ({count}/3)
      </div>
    </div>
  );
}

// ==========================================
// 2) Bubbles
// ==========================================
function Bubbles({ layer, onWin }) {
  const { language, soundOn } = useGame();
  const [bubbles, setBubbles] = useState(() =>
    Array.from({ length: 8 }, (_, i) => ({
      id: i,
      left: 10 + Math.random() * 75,
      top: 15 + Math.random() * 65,
      size: 70 + Math.random() * 50,
      color: ["#FCA5A5", "#FCD34D", "#86EFAC", "#7DD3FC", "#D8B4FE"][i % 5],
    }))
  );

  const pop = (id) => {
    playSfxHelper("pop", soundOn);
    setBubbles((b) => b.filter((x) => x.id !== id));
  };

  useEffect(() => {
    if (bubbles.length === 0) onWin();
  }, [bubbles, onWin]);

  return (
    <div
      className="relative w-full rounded-3xl bg-white/40 backdrop-blur min-h-[60vh] overflow-hidden border-4 border-white"
      data-testid="game-bubbles"
    >
      <div className="absolute top-3 left-0 right-0 text-center font-display font-bold text-xl sm:text-2xl text-slate-700">
        {t("pop_them", language)}
      </div>
      {bubbles.map((b) => (
        <motion.button
          key={b.id}
          onClick={() => pop(b.id)}
          initial={{ scale: 0 }}
          animate={{ scale: 1, y: [0, -10, 0] }}
          exit={{ scale: 0 }}
          transition={{ y: { duration: 2 + Math.random() * 2, repeat: Infinity } }}
          whileTap={{ scale: 1.3 }}
          className="absolute rounded-full"
          style={{
            left: `${b.left}%`,
            top: `${b.top}%`,
            width: b.size,
            height: b.size,
            background: `radial-gradient(circle at 30% 30%, white, ${b.color})`,
            boxShadow: `0 6px 0 ${b.color}aa`,
          }}
          data-testid={`bubble-${b.id}`}
        />
      ))}
    </div>
  );
}

// ==========================================
// 3) Touch & Sound
// ==========================================
function TouchSound({ layer, onWin }) {
  const { language, soundOn } = useGame();
  const [taps, setTaps] = useState(0);
  const items = [
    { emoji: "🔔", sfx: "tap", color: "#FCD34D" },
    { emoji: "🥁", sfx: "pop", color: "#F87171" },
    { emoji: "🎵", sfx: "giggle", color: "#86EFAC" },
    { emoji: "⭐", sfx: "star", color: "#7DD3FC" },
  ];

  const handle = (sfx) => {
    playSfxHelper(sfx, soundOn);
    const n = taps + 1;
    setTaps(n);
    if (n >= 6) {
      setTimeout(() => onWin(), 500);
      setTaps(0);
    }
  };

  return (
    <div className="flex flex-col items-center gap-6 mt-4" data-testid="game-touch-sound">
      <div className="font-display text-xl sm:text-2xl font-bold text-slate-700">
        {language === "id" ? "Ketuk semua benda!" : "Tap all items!"} ({taps}/6)
      </div>
      <div className="grid grid-cols-2 gap-6 max-w-md">
        {items.map((it) => (
          <motion.button
            key={it.emoji}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.85, rotate: [0, -10, 10, 0] }}
            onClick={() => handle(it.sfx)}
            className="w-32 h-32 sm:w-40 sm:h-40 rounded-3xl flex items-center justify-center text-7xl"
            style={{ background: it.color, boxShadow: `0 8px 0 ${it.color}cc`, border: "4px solid white" }}
            data-testid={`touch-item-${it.emoji}`}
          >
            {it.emoji}
          </motion.button>
        ))}
      </div>
    </div>
  );
}

// ==========================================
// 4) High Contrast Tracker (Game 4)
// ==========================================
function HighContrastTracker({ layer, onWin }) {
  const { language, soundOn } = useGame();
  const [count, setCount] = useState(0);
  const [position, setPosition] = useState({ x: 10, y: 30 });
  const [direction, setDirection] = useState(1);

  useEffect(() => {
    const timer = setInterval(() => {
      setPosition((pos) => {
        let newX = pos.x + 2 * direction;
        let newDir = direction;
        if (newX > 80) newDir = -1;
        if (newX < 10) newDir = 1;
        setDirection(newDir);
        return {
          x: newX,
          y: 40 + Math.sin(newX / 8) * 15,
        };
      });
    }, 60);
    return () => clearInterval(timer);
  }, [direction]);

  const handleTap = () => {
    playSfxHelper("pop", soundOn);
    const newCount = count + 1;
    setCount(newCount);
    // Relocate with an extra bounce
    setDirection((d) => -d);
    if (newCount >= 3) {
      setTimeout(() => onWin(), 600);
    }
  };

  return (
    <div className="relative w-full rounded-3xl bg-slate-950 min-h-[60vh] overflow-hidden border-4 border-white flex flex-col justify-between p-4">
      <div className="text-center font-display font-bold text-lg sm:text-xl text-white tracking-wider">
        {language === "id" ? "Ikuti & Ketuk Pola!" : "Track & Tap the Pattern!"} ({count}/3)
      </div>

      <motion.button
        onClick={handleTap}
        style={{
          left: `${position.x}%`,
          top: `${position.y}%`,
        }}
        animate={{ scale: [1, 1.05, 1] }}
        transition={{ repeat: Infinity, duration: 1.5 }}
        className="absolute w-24 h-24 sm:w-32 sm:h-32 rounded-full border-4 border-red-500 bg-black flex items-center justify-center overflow-hidden shadow-2xl"
      >
        {/* High contrast chess pattern */}
        <div className="grid grid-cols-2 w-full h-full animate-spin [animation-duration:10s]">
          <div className="bg-white" />
          <div className="bg-black" />
          <div className="bg-black" />
          <div className="bg-white" />
        </div>
        <div className="absolute w-8 h-8 rounded-full bg-red-600 border-2 border-white shadow-lg" />
      </motion.button>

      <div className="text-center text-xs font-bold text-slate-400">
        {language === "id" ? "Rangsangan Visual Kontras Tinggi" : "High Contrast Visual Stimulation"}
      </div>
    </div>
  );
}

// ==========================================
// 5) Tap to Drum (Game 5)
// ==========================================
function TapToDrum({ layer, onWin }) {
  const { language, soundOn } = useGame();
  const [taps, setTaps] = useState(0);
  const [ripples, setRipples] = useState([]);

  const handleDrumTap = (e) => {
    playSfxHelper("woof", soundOn); // Low pitch bark as drum sound substitute
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const newRipple = {
      id: Date.now(),
      x,
      y,
    };
    setRipples((r) => [...r, newRipple]);

    const newTaps = taps + 1;
    setTaps(newTaps);
    if (newTaps >= 5) {
      setTimeout(() => onWin(), 600);
    }
  };

  return (
    <div className="flex flex-col items-center gap-6 mt-4">
      <div className="font-display text-xl sm:text-2xl font-bold text-slate-700 text-center">
        {language === "id" ? "Ketuk Kendang Lucu!" : "Tap the Cute Drum!"} ({taps}/5)
      </div>

      <motion.button
        whileTap={{ scale: 0.92, rotate: [-2, 2, -2] }}
        onClick={handleDrumTap}
        className="relative w-64 h-64 sm:w-80 sm:h-80 rounded-full border-[10px] border-amber-800 bg-radial-gradient flex items-center justify-center overflow-hidden shadow-2xl"
        style={{
          background: "radial-gradient(circle, #FDE68A 30%, #D97706 100%)",
          boxShadow: "0 12px 0 #92400E, inset 0 8px 16px rgba(0,0,0,0.1)",
        }}
      >
        {/* Ripples effect inside drum */}
        <AnimatePresence>
          {ripples.map((rp) => (
            <motion.div
              key={rp.id}
              initial={{ scale: 0, opacity: 0.8 }}
              animate={{ scale: 4, opacity: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.6 }}
              className="absolute rounded-full border-4 border-amber-900 pointer-events-none"
              style={{
                left: rp.x - 20,
                top: rp.y - 20,
                width: 40,
                height: 40,
              }}
            />
          ))}
        </AnimatePresence>

        <span className="text-8xl select-none z-10">🥁</span>
      </motion.button>

      <div className="font-bold text-slate-500 text-sm">
        {language === "id" ? "Merangsang Koordinasi Motorik & Refleks" : "Stimulates Motor Coordination & Reflexes"}
      </div>
    </div>
  );
}

// ==========================================
// 6) Baby Farmyard (Game 6)
// ==========================================
function BabyFarmyard({ layer, onWin }) {
  const { language, soundOn } = useGame();
  const [visited, setVisited] = useState({});

  const animals = [
    { key: "cat", emoji: "🐱", sound: "meow", color: "#FDE68A" },
    { key: "dog", emoji: "🐶", sound: "woof", color: "#FCA5A5" },
    { key: "duck", emoji: "🦆", sound: "quack", color: "#86EFAC" },
  ];

  const handleAnimalClick = (key, sound) => {
    playSfxHelper(sound, soundOn);
    const updated = { ...visited, [key]: true };
    setVisited(updated);
    if (Object.keys(updated).length === animals.length) {
      setTimeout(() => onWin(), 1000);
    }
  };

  return (
    <div className="flex flex-col items-center gap-6 mt-4">
      <div className="font-display text-xl sm:text-2xl font-bold text-slate-700 text-center">
        {language === "id" ? "Dengarkan Suara Hewan!" : "Listen to the Animal Sounds!"}
      </div>

      <div className="flex flex-wrap gap-4 justify-center">
        {animals.map((an) => {
          const isVisited = !!visited[an.key];
          return (
            <motion.button
              key={an.key}
              whileHover={{ y: -8 }}
              whileTap={{ scale: 0.9 }}
              onClick={() => handleAnimalClick(an.key, an.sound)}
              className="w-28 h-36 sm:w-36 sm:h-48 rounded-[2rem] border-4 border-white shadow-xl flex flex-col items-center justify-center gap-4 relative overflow-hidden"
              style={{ background: an.color }}
            >
              {isVisited && (
                <div className="absolute top-2 right-2 bg-emerald-500 text-white rounded-full p-1 text-xs">
                  ✅
                </div>
              )}
              <span className="text-6xl sm:text-7xl">{an.emoji}</span>
              <span className="font-display font-extrabold text-slate-700 uppercase text-xs sm:text-sm">
                {an.key === "cat" ? (language === "id" ? "Kucing" : "Cat") : ""}
                {an.key === "dog" ? (language === "id" ? "Anjing" : "Dog") : ""}
                {an.key === "duck" ? (language === "id" ? "Bebek" : "Duck") : ""}
              </span>
            </motion.button>
          );
        })}
      </div>
      <div className="font-bold text-slate-500 text-xs">
        {language === "id" ? "Latih Asosiasi Visual & Suara" : "Train Visual & Sound Associations"}
      </div>
    </div>
  );
}

// ==========================================
// 7) Catch the Glow (Game 7)
// ==========================================
function CatchTheGlow({ layer, onWin }) {
  const { language, soundOn } = useGame();
  const [count, setCount] = useState(0);
  const [position, setPosition] = useState({ x: 30, y: 40 });

  const moveGlow = () => {
    setPosition({
      x: 15 + Math.random() * 70,
      y: 20 + Math.random() * 60,
    });
  };

  useEffect(() => {
    const interval = setInterval(moveGlow, 1800);
    return () => clearInterval(interval);
  }, []);

  const handleTap = () => {
    playSfxHelper("star", soundOn);
    const newCount = count + 1;
    setCount(newCount);
    moveGlow();
    if (newCount >= 3) {
      setTimeout(() => onWin(), 600);
    }
  };

  return (
    <div className="relative w-full rounded-3xl bg-slate-900 min-h-[60vh] overflow-hidden border-4 border-white flex flex-col justify-between p-4">
      <div className="text-center font-display font-bold text-lg sm:text-xl text-yellow-300">
        {language === "id" ? "Tangkap Kunang-Kunang! 🌟" : "Catch the Firefly! 🌟"} ({count}/3)
      </div>

      <motion.button
        onClick={handleTap}
        style={{
          left: `${position.x}%`,
          top: `${position.y}%`,
        }}
        className="absolute w-20 h-20 rounded-full flex items-center justify-center"
        animate={{ scale: [1, 1.2, 1], opacity: [0.8, 1, 0.8] }}
        transition={{ repeat: Infinity, duration: 1.2 }}
      >
        {/* Firefly glow */}
        <div className="absolute inset-0 bg-yellow-400 rounded-full blur-xl opacity-70 animate-pulse" />
        <span className="text-5xl select-none z-10">🪰</span>
      </motion.button>

      <div className="text-center text-xs font-bold text-slate-400">
        {language === "id" ? "Melatih Fokus Visual & Koordinasi Cepat" : "Trains Visual Focus & Quick Coordination"}
      </div>
    </div>
  );
}

// ==========================================
// 8) Falling Toys (Game 8)
// ==========================================
function FallingToys({ layer, onWin }) {
  const { language, soundOn } = useGame();
  const [toys, setToys] = useState([]);
  const [caughtCount, setCaughtCount] = useState(0);

  const toyTypes = [
    { emoji: "🧸", label: "bear" },
    { emoji: "🎈", label: "balloon" },
    { emoji: "🐤", label: "duck" },
    { emoji: "🚗", label: "car" },
  ];

  // Spawn toys periodically
  useEffect(() => {
    let active = true;
    const spawn = () => {
      if (!active) return;
      const type = toyTypes[Math.floor(Math.random() * toyTypes.length)];
      const newToy = {
        id: Date.now() + Math.random(),
        emoji: type.emoji,
        left: 10 + Math.random() * 80,
        delay: 0,
      };
      setToys((t) => [...t, newToy]);

      // Remove after falling duration if not clicked
      setTimeout(() => {
        setToys((t) => t.filter((x) => x.id !== newToy.id));
      }, 4000);
    };

    const interval = setInterval(spawn, 1500);
    return () => {
      active = false;
      clearInterval(interval);
    };
  }, []);

  const catchToy = (id) => {
    playSfxHelper("pop", soundOn);
    setToys((t) => t.filter((x) => x.id !== id));
    const newCount = caughtCount + 1;
    setCaughtCount(newCount);
    if (newCount >= 4) {
      setTimeout(() => onWin(), 600);
    }
  };

  return (
    <div className="relative w-full rounded-3xl bg-sky-50 min-h-[60vh] overflow-hidden border-4 border-white flex flex-col justify-between p-4">
      <div className="text-center font-display font-bold text-lg sm:text-xl text-sky-800 z-10">
        {language === "id" ? "Ketuk Mainan Jatuh!" : "Tap the Falling Toys!"} ({caughtCount}/4)
      </div>

      <div className="absolute inset-0">
        {toys.map((ty) => (
          <motion.button
            key={ty.id}
            onClick={() => catchToy(ty.id)}
            initial={{ y: -60, x: `${ty.left}%` }}
            animate={{ y: "100%", rotate: 360 }}
            transition={{ duration: 4.5, ease: "linear" }}
            className="absolute text-5xl sm:text-6xl p-2 select-none"
          >
            {ty.emoji}
          </motion.button>
        ))}
      </div>

      <div className="text-center text-xs font-bold text-sky-500 z-10">
        {language === "id" ? "Melatih Respon Motorik & Persepsi Gerak" : "Trains Motor Response & Motion Perception"}
      </div>
    </div>
  );
}

// ==========================================
// 9) Mascot Mimic (Game 9)
// ==========================================
function MascotMimic({ layer, onWin }) {
  const { language, soundOn } = useGame();
  const [moodCount, setMoodCount] = useState(0);

  const moods = [
    { emoji: "😆", label: "Happy", sfx: "giggle", color: "#FDE68A" },
    { emoji: "😮", label: "Surprised", sfx: "pop", color: "#7DD3FC" },
    { emoji: "🥰", label: "Loved", sfx: "star", color: "#FCA5A5" },
  ];

  const currentMood = moods[moodCount % moods.length];

  const handleMascotClick = () => {
    playSfxHelper(currentMood.sfx, soundOn);
    const next = moodCount + 1;
    setMoodCount(next);
    if (next >= 3) {
      setTimeout(() => onWin(), 1000);
    }
  };

  return (
    <div className="flex flex-col items-center gap-6 mt-4">
      <div className="font-display text-xl sm:text-2xl font-bold text-slate-700 text-center">
        {language === "id" ? "Ketuk Ekspresi Maskot!" : "Tap the Mascot's Expression!"}
      </div>

      <motion.button
        onClick={handleMascotClick}
        whileTap={{ scale: 0.9, rotate: [0, -10, 10, 0] }}
        animate={{ y: [0, -15, 0] }}
        transition={{ repeat: Infinity, duration: 2 }}
        className="w-56 h-56 sm:w-64 sm:h-64 rounded-full border-[8px] border-white shadow-2xl flex items-center justify-center text-[10rem] select-none"
        style={{ background: currentMood.color }}
      >
        {currentMood.emoji}
      </motion.button>

      <div className="text-center font-bold text-slate-500 text-sm max-w-xs leading-snug">
        {language === "id"
          ? `Ekspresi: ${currentMood.label === "Happy" ? "Gembira" : currentMood.label === "Surprised" ? "Terkejut" : "Penuh Kasih"}`
          : `Mood: ${currentMood.label}`}
        <span className="block text-xs font-normal text-slate-400 mt-2">
          {language === "id"
            ? "Membangun Kecerdasan Emosi & Empati Awal"
            : "Builds Emotional Intelligence & Early Empathy"}
        </span>
      </div>
    </div>
  );
}

// ==========================================
// 10) Silhouette Matcher (Game 10)
// ==========================================
function SilhouetteMatcher({ layer, onWin }) {
  const { language, soundOn } = useGame();
  const [success, setSuccess] = useState(false);

  const handleMatch = () => {
    playSfxHelper("success", soundOn);
    setSuccess(true);
    setTimeout(() => onWin(), 1500);
  };

  return (
    <div className="flex flex-col items-center gap-6 mt-4">
      <div className="font-display text-xl sm:text-2xl font-bold text-slate-700 text-center">
        {language === "id" ? "Cocokkan Bayangan!" : "Match the Silhouette!"}
      </div>

      <div className="flex items-center justify-center gap-12 mt-6">
        {/* Silhouette target */}
        <div className="w-32 h-32 rounded-3xl bg-slate-400/60 border-4 border-slate-400 flex items-center justify-center text-7xl select-none relative">
          <span className={success ? "opacity-100 transition-opacity duration-500" : "opacity-25 grayscale brightness-0"}>
            ⭐
          </span>
          {!success && <span className="absolute text-sm font-extrabold text-slate-700">?</span>}
        </div>

        {/* Separator arrow */}
        <span className="text-4xl text-slate-400">➡️</span>

        {/* Source item button */}
        <motion.button
          onClick={handleMatch}
          disabled={success}
          whileHover={success ? {} : { scale: 1.1 }}
          whileTap={success ? {} : { scale: 0.9 }}
          animate={success ? { rotate: 360, scale: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="w-32 h-32 rounded-3xl bg-amber-300 border-4 border-white shadow-xl flex items-center justify-center text-7xl select-none"
        >
          ⭐
        </motion.button>
      </div>

      <div className="font-bold text-slate-500 text-sm mt-4">
        {language === "id"
          ? "Membantu Kategorisasi Bentuk Dasar & Logika"
          : "Helps Basic Shape Categorization & Logic"}
      </div>
    </div>
  );
}

// ==========================================
// 11) Shake the Rattle (Game 11)
// ==========================================
function ShakeRattle({ layer, onWin }) {
  const { language, soundOn } = useGame();
  const [shakes, setShakes] = useState(0);
  const [shaking, setShaking] = useState(false);

  const handleShake = () => {
    if (shaking) return;
    playSfxHelper("star", soundOn); // Kring substitute
    setShaking(true);
    const newShakes = shakes + 1;
    setShakes(newShakes);
    
    setTimeout(() => {
      setShaking(false);
      if (newShakes >= 5) {
        onWin();
      }
    }, 400);
  };

  return (
    <div className="flex flex-col items-center gap-6 mt-4">
      <div className="font-display text-xl sm:text-2xl font-bold text-slate-700 text-center">
        {language === "id" ? "Goyangkan Kerincingan! 🪇" : "Shake the Rattle! 🪇"} ({shakes}/5)
      </div>

      <motion.button
        onClick={handleShake}
        animate={shaking ? {
          rotate: [-15, 15, -15, 15, 0],
          x: [-5, 5, -5, 5, 0],
        } : {}}
        transition={{ duration: 0.35 }}
        className="w-56 h-56 rounded-full bg-emerald-100 border-[8px] border-white shadow-2xl flex items-center justify-center text-[8rem] select-none"
      >
        🪇
      </motion.button>

      <div className="text-center font-bold text-slate-500 text-sm max-w-xs leading-snug">
        {language === "id"
          ? "Rangsangan Auditorik-Motorik Halus"
          : "Auditory-Fine Motor Stimulation"}
      </div>
    </div>
  );
}

// ==========================================
// 12) Fog Sweeper (Game 12)
// ==========================================
function FogSweeper({ layer, onWin }) {
  const { language, soundOn } = useGame();
  const [revealed, setRevealed] = useState(false);
  const [patches, setPatches] = useState(() => 
    Array.from({ length: 9 }, (_, i) => ({
      id: i,
      wiped: false,
    }))
  );

  const wipePatch = (id) => {
    if (revealed) return;
    playSfxHelper("pop", soundOn);
    setPatches((prev) => {
      const updated = prev.map((p) => (p.id === id ? { ...p, wiped: true } : p));
      const wipedCount = updated.filter((p) => p.wiped).length;
      if (wipedCount === prev.length) {
        setRevealed(true);
        setTimeout(() => onWin(), 1200);
      }
      return updated;
    });
  };

  return (
    <div className="flex flex-col items-center gap-6 mt-4">
      <div className="font-display text-xl sm:text-2xl font-bold text-slate-700 text-center">
        {language === "id" ? "Usap Embun di Kaca! 🌫️" : "Wipe the Fog! 🌫️"}
      </div>

      <div className="relative w-64 h-64 sm:w-80 sm:h-80 rounded-3xl overflow-hidden border-4 border-white shadow-2xl bg-amber-100 flex items-center justify-center">
        <motion.div
          animate={revealed ? { scale: [1, 1.2, 1], rotate: [0, 10, -10, 0] } : {}}
          transition={{ duration: 1 }}
          className="text-9xl select-none"
        >
          ⭐
        </motion.div>

        {!revealed && (
          <div className="absolute inset-0 grid grid-cols-3 grid-rows-3 gap-0.5 bg-slate-300/40">
            {patches.map((p) => (
              <motion.button
                key={p.id}
                onMouseEnter={() => wipePatch(p.id)}
                onTouchStart={() => wipePatch(p.id)}
                onClick={() => wipePatch(p.id)}
                animate={{ opacity: p.wiped ? 0 : 1 }}
                className="w-full h-full bg-slate-200/90 backdrop-blur-md flex items-center justify-center text-slate-400 font-bold border border-white/20 text-xs select-none"
              >
                🌫️
              </motion.button>
            ))}
          </div>
        )}
      </div>

      <div className="text-center font-bold text-slate-500 text-sm max-w-xs leading-snug">
        {language === "id"
          ? "Melatih Pemahaman Sebab-Akibat & Eksplorasi Taktil"
          : "Trains Cause-Effect Rationale & Tactile Exploration"}
      </div>
    </div>
  );
}

// Export Layer 1 games list
export const Layer1Games = {
  peekaboo: { icon: "🙈", Component: PeekABoo },
  bubbles: { icon: "🫧", Component: Bubbles },
  touch_sound: { icon: "🔔", Component: TouchSound },
  contrast: { icon: "🏁", Component: HighContrastTracker },
  drum: { icon: "🥁", Component: TapToDrum },
  farmyard: { icon: "🐱", Component: BabyFarmyard },
  glow: { icon: "🌟", Component: CatchTheGlow },
  falling: { icon: "🧸", Component: FallingToys },
  emotions: { icon: "😆", Component: MascotMimic },
  silhouette: { icon: "⭐", Component: SilhouetteMatcher },
  rattle: { icon: "🪇", Component: ShakeRattle },
  sweeper: { icon: "🌫️", Component: FogSweeper },
};
